import { useMemo } from 'react';
import { useAuth } from './useAuth';
import {
  canAccess,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canStayOnModule as stayOnModule,
  hasAnyPermissionWithLegacy as anyPermissionWithLegacy,
} from './permissions';

/**
 * Hook for checking permissions in components
 *
 * @example
 * const { canAccess, hasPermission } = usePermissions();
 * if (canAccess('claims')) { ... }
 * if (hasPermission('claims.approve')) { ... }
 */
export function usePermissions() {
  const { permissions, user, accessModules } = useAuth();

  return useMemo(() => ({
    permissions,
    canAccess: (moduleKey) => canAccess(moduleKey, permissions),
    hasPermission: (permission) => hasPermission(permission, permissions),
    hasAnyPermission: (permissionList) => hasAnyPermission(permissionList, permissions),
    hasAllPermissions: (permissionList) => hasAllPermissions(permissionList, permissions),
    canStayOnModule: (moduleKey, legacyRoleIds = []) =>
      stayOnModule(moduleKey, permissions, user, legacyRoleIds),
    // Permission-first action gate; legacy staff role ids apply only on an empty map.
    hasAnyPermissionWithLegacy: (permissionList, legacyRoleIds = []) =>
      anyPermissionWithLegacy(permissionList, permissions, user, legacyRoleIds),
  }), [permissions, user, accessModules]);
}

export default usePermissions;


